'use client'

import { useEffect, useState } from 'react'
import type { FormSchema } from '@school-journey/shared-types/schemas'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FormRenderer } from '@/components/form-renderer/form-renderer'

// The signer envelope threaded back into the order body's `waiverSignatures`.
// signedAt/signatureMethod are synthesized here because FormRenderer.onSubmit
// does not emit them (KTD2).
export interface InlineWaiverResult {
  formResponsePublicId: string
  signerName: string
  signerEmail: string
  signedAt: string
  signatureMethod: string
}

interface InlineWaiverSignerProps {
  apiBase: string
  formPublicSlug: string
  ticketedEventPublicId: string
  waiverRequirementPublicId: string
  // Buyer identity — mirrors the popup path, which records the buyer as signer
  // (NOT the renderer's own signerIdentity), keeping buyerLocked consistent.
  signerName: string
  signerEmail: string
  label: string
  onSigned: (result: InlineWaiverResult) => void
  onBack: () => void
}

type State =
  | { kind: 'loading' }
  | {
      kind: 'ready'
      form: { title: string; description: string | null; schoolName: string | null; schema: FormSchema }
    }
  | {
      kind: 'submitting'
      form: { title: string; description: string | null; schoolName: string | null; schema: FormSchema }
    }
  | { kind: 'error'; message: string }

/**
 * Inline (in-modal) at-registration signing step for embed mode. Reuses the
 * public-form-client pre-order signing path — fetch schema -> FormRenderer ->
 * submit with `waiverContext` -> `formResponsePublicId` — but renders inline and
 * hands the result back to the RSVP flow instead of posting through a popup, so
 * the signature exists BEFORE the order and folds into `waiverSignatures` (KTD2).
 */
export function InlineWaiverSigner({
  apiBase,
  formPublicSlug,
  ticketedEventPublicId,
  waiverRequirementPublicId,
  signerName,
  signerEmail,
  label,
  onSigned,
  onBack
}: InlineWaiverSignerProps) {
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const res = await fetch(`${apiBase}/v1/forms/public/${encodeURIComponent(formPublicSlug)}`, {
          credentials: 'include'
        })
        if (cancelled) return
        if (!res.ok) {
          setState({ kind: 'error', message: 'This waiver isn’t available to sign right now.' })
          return
        }
        const json = (await res.json()) as {
          form: { title: string; description: string | null; schoolName: string | null; schema: FormSchema }
        }
        setState({ kind: 'ready', form: json.form })
      } catch {
        if (!cancelled) setState({ kind: 'error', message: 'Could not load the waiver. Please try again.' })
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [apiBase, formPublicSlug])

  if (state.kind === 'loading') {
    return (
      <div className="flex flex-col gap-4">
        <BackBar label={label} onBack={onBack} />
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="flex flex-col gap-4">
        <BackBar label={label} onBack={onBack} />
        <p className="text-sm text-destructive">{state.message}</p>
      </div>
    )
  }

  const form = state.form
  return (
    <div className="flex flex-col gap-4">
      <BackBar label={label} onBack={onBack} />
      <FormRenderer
        schema={form.schema}
        title={form.title}
        description={form.description}
        schoolName={form.schoolName}
        submitting={state.kind === 'submitting'}
        onSubmit={async (payload, signature) => {
          setState({ kind: 'submitting', form })
          try {
            const honeypot = typeof payload._hp === 'string' ? payload._hp : ''
            const cleanPayload = { ...payload }
            delete cleanPayload._hp
            const res = await fetch(`${apiBase}/v1/forms/public/${encodeURIComponent(formPublicSlug)}/submit`, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                payload: cleanPayload,
                signature: signature ?? undefined,
                honeypot: honeypot || undefined,
                waiverContext: { ticketedEventPublicId, waiverRequirementPublicId, signerEmail, signerName }
              })
            })
            if (!res.ok) {
              const json = (await res.json().catch(() => ({}))) as { error?: { message?: string } }
              setState({ kind: 'error', message: json.error?.message ?? 'Submission failed. Please try again.' })
              return
            }
            const json = (await res.json().catch(() => ({}))) as { data?: { submissionPublicId?: string } }
            const formResponsePublicId = json.data?.submissionPublicId
            if (!formResponsePublicId) {
              setState({ kind: 'error', message: 'Submission failed. Please try again.' })
              return
            }
            onSigned({
              formResponsePublicId,
              signerName,
              signerEmail,
              signedAt: new Date().toISOString(),
              signatureMethod: 'typed'
            })
          } catch {
            setState({ kind: 'error', message: 'Submission failed. Please try again.' })
          }
        }}
      />
    </div>
  )
}

function BackBar({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-3">
      <Button type="button" variant="outline" size="sm" onClick={onBack}>
        ← Back
      </Button>
      <span className="text-sm font-medium">{label}</span>
    </div>
  )
}
